import { GamePlayer, GameSetup, type Tee } from '@/types/golf';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonModal,
  IonNav,
  IonToolbar
} from '@ionic/react';
import TeeBadge from './TeeBadge';
import { closeOutline, trashOutline } from 'ionicons/icons';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GolfHandicapInput from '../GolfHandicapInput';
import TeeSelectionPage from './TeeSelectionPage';
import { GolfAvatar } from '../ui/GolfAvatar';
import AvatarUpload from '../AvatarUpload';
import player from 'video.js/dist/types/player';

interface PlayerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: GamePlayer;
  gameSetup?: Partial<GameSetup>;
  course?: any; // Direct course object
  onUpdatePlayer: (updatedPlayer: GamePlayer) => void;
  onDeletePlayer: (playerId: string) => void;
  isCurrentUser?: boolean;
  onSave?: (updatedPlayer: GamePlayer) => void; // Optional save handler
}

const PlayerEditModal: React.FC<PlayerEditModalProps> = ({
  isOpen,
  onClose,
  player,
  gameSetup,
  course,
  onUpdatePlayer,
  onDeletePlayer,
  isCurrentUser = false,
  onSave
}) => {
  // If player is null, don't render the modal content to prevent errors
  if (!player) {
    return null;
  }

  const { t } = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  // We're not using name in this component as it's just for display
  const [handicap, setHandicap] = useState(player.handicap?.toString() || '0');

  // Get available tees from the course (either from gameSetup or direct course prop)
  const availableTees = course?.tees || gameSetup?.course?.tees || [];
  const [teeBox, setTeeBox] = useState<Tee>(
    availableTees.find((tee: Tee) => tee.teeId === player.teeBoxId) ||
    availableTees.find((tee: Tee) => tee.teeName === player.teeName) ||
    availableTees[0]
  );
  // We're not using email in this component
  const navRef = useRef<HTMLIonNavElement>(null);

  const handleSave = () => {
    const updatedPlayer: GamePlayer = {
      ...player,
      name: player.name, // Use the original name since we're not editing it in this modal
      handicap: parseInt(handicap) || 0,
      teeBoxId: teeBox.teeId,
      teeName: teeBox.teeName,
      email: player.email // Use the original email since we're not editing it in this modal
    };

    // Call both handlers if provided
    onUpdatePlayer(updatedPlayer);
    if (onSave) {
      onSave(updatedPlayer);
    }
    onClose();
  };

  const handleDelete = () => {
    onDeletePlayer(player.userId); // Use userId instead of id
    onClose();
  };


  // Navigate to tee selection page
  const navigateToTeeSelection = () => {
    if (navRef.current) {
      navRef.current.push(
        (props: any) => (
          <TeeSelectionPage
            {...props}
            nav={navRef.current!}
            tees={availableTees}
            selectedTee={teeBox}
            onSelectTee={(tee) => setTeeBox(tee)}
          />
        )
      );
    }
  };

  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.75}
      breakpoints={[0, 0.75]}
    >
      <IonHeader className="ion-no-border">
        <IonToolbar color="light">
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent color="light" className="ion-padding">
        <IonNav ref={navRef} root={() => (
          <div className="p-4 space-y-6">
            {/* Player Avatar */}
            <div className="flex flex-col items-center mb-6">
              {isCurrentUser ? (
                <AvatarUpload
                  initialImage={player.imageUrl}
                  className="aspect-square"
                  onAvatarUpdated={(url) => {
                    // Update player with new avatar URL
                    onUpdatePlayer({
                      ...player,
                      imageUrl: url
                    });
                  }}
                />
              ) : (
                <GolfAvatar imageUrl={player.imageUrl} className="aspect-square size-24 rounded-lg" />
              )}
              <h2 className="text-xl font-bold">{player.name}</h2>
              <div className="text-sm text-gray-500">{t('France')}</div>
            </div>

            <div className="space-y-4">
              {/* Tee Selection */}
              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  {t('tee')}
                </div>
                <IonItem
                  button
                  detail
                  lines="none"
                  className="rounded-lg border "
                  onClick={navigateToTeeSelection}
                >
                  <div className="flex items-center gap-3 py-3 w-full">
                    <TeeBadge teeName={teeBox.teeName} size="md" />
                    <div>
                      <div className="font-medium">
                        {t(teeBox.teeName) + " (" + teeBox.totalDistance + "m)"}
                      </div>
                      <div className="text-xs text-gray-500">
                        CR: {teeBox.courseCr}
                        SR: {teeBox.courseSlope}
                      </div>
                    </div>
                  </div>
                </IonItem>
              </div>

              {/* Handicap */}
              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  {t('handicap')}
                </div>
                <GolfHandicapInput
                  fill='outline'
                  initialValue={Number(handicap)}
                  onChange={(value) => setHandicap(value)}
                />
              </div>
              <div className="p-4">
                <IonButton
                  expand="block"
                  shape="round"
                  onClick={handleSave}
                  className="mb-2"
                >
                  {t('save')}
                </IonButton>

                {!isCurrentUser && (
                  <IonButton
                    expand="block"
                    color="danger"
                    fill="clear"
                    shape="round"
                    onClick={handleDelete}
                    className="text-red-500"
                  >
                    <div className="flex items-center gap-2">
                      <IonIcon icon={trashOutline} />
                      {t('delete_player')}
                    </div>
                  </IonButton>
                )}
              </div>
            </div>
          </div>
        )} />
      </IonContent>
    </IonModal>
  );
};

export default PlayerEditModal;
